import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MarketplaceAPI, PAGE_SIZE } from '../lib/marketplace';
import { adaptCanonicalToProperty } from '../lib/canonical-adapter';
import { Property, FilterState, SortOption } from '../types';

// Helper to safely process potentially null dates (though type says string)
const processDate = (d: string | undefined) => d || new Date(0).toISOString();

export function useProperties() {
    // Determine initial filters
    const initialFilters = useMemo<FilterState>(() => ({
        searchQuery: '', locations: [], priceMin: '', priceMax: '', beds: null, baths: null,
        type: [], developer: 'All Developers', completion: [],
        sqftMin: '', sqftMax: '', ppsfMin: '', ppsfMax: '',
        priceRange: [0, 50000000], // Default 0 to 50M
        sqftRange: [0, 10000],
        ppsfRange: [0, 20000]
    }), []);

    const [filters, setFilters] = useState<FilterState>(initialFilters);
    const [sortOption, setSortOption] = useState<SortOption>('date'); // Default: Newest First
    const [currentPage, setCurrentPage] = useState(1);
    
    // --- DATA LOADING ---
    const normalizedFilters = useMemo<FilterState>(() => ({
        ...initialFilters,
        ...filters
    }), [filters, initialFilters]);

    const filterDeps = [
        sortOption,
        normalizedFilters.searchQuery,
        normalizedFilters.priceRange[0], normalizedFilters.priceRange[1],
        normalizedFilters.beds,
        normalizedFilters.sqftRange[0], normalizedFilters.sqftRange[1],
        normalizedFilters.ppsfRange[0], normalizedFilters.ppsfRange[1],
        normalizedFilters.type.join(','),
        normalizedFilters.locations.join(','),
        normalizedFilters.developer,
        normalizedFilters.completion.join(',')
    ];

    const buildQueryParams = () => {
        const params: any = {};
        if (normalizedFilters.searchQuery) params.search_query = normalizedFilters.searchQuery;
        const [minP, maxP] = normalizedFilters.priceRange;
        if (minP > 0) params.unit_price_from = minP;
        if (maxP < 50000000) params.unit_price_to = maxP;
        if (normalizedFilters.beds !== null) {
            const beds = [];
            for (let i = normalizedFilters.beds; i <= 8; i++) beds.push(i);
            params.bedrooms = beds.join(',');
        }
        if (normalizedFilters.developer && normalizedFilters.developer !== 'All Developers') {
            params.developer = normalizedFilters.developer;
        }
        if (normalizedFilters.locations && normalizedFilters.locations.length > 0) {
            params.locations = normalizedFilters.locations;
        }
        if (normalizedFilters.type && normalizedFilters.type.length > 0) {
            params.property_types = normalizedFilters.type;
        }
        const [minSqft, maxSqft] = normalizedFilters.sqftRange;
        if (minSqft > 0) params.sqft_min = minSqft;
        if (maxSqft < 10000) params.sqft_max = maxSqft;
        const [minPpsf, maxPpsf] = normalizedFilters.ppsfRange;
        if (minPpsf > 0) params.ppsf_min = minPpsf;
        if (maxPpsf > 0 && maxPpsf < 20000) params.ppsf_max = maxPpsf;

        switch (sortOption) {
            case 'price_asc': params.ordering = 'unit_price_from'; break;
            case 'price_desc': params.ordering = '-unit_price_from'; break;
            case 'date': params.ordering = '-source_updated_at'; break; // Reelly API uses updated_at, not created_at
        }
        return params;
    };

    const {
        data: pageData,
        isPending: isPagePending,
        isFetching: isPageFetching,
        isError: isPageError,
        error: pageError
    } = useQuery({
        queryKey: ['projects', 'page', currentPage, ...filterDeps],
        queryFn: async () => {
            const params = buildQueryParams();
            return MarketplaceAPI.getProjects({ ...params, page: currentPage, limit: PAGE_SIZE });
        },
        staleTime: 5 * 60 * 1000,
    });

    // Only fetch all data when client-side search filtering is needed
    // Server-side sorting is already handled via the 'ordering' parameter
    const needsBulkFetch = !!normalizedFilters.searchQuery; // Search requires client-side filtering
    
    const { 
        data: allData,
        isPending: isAllPending,
        isFetching: isAllFetching
    } = useQuery({
        queryKey: ['projects', 'all', ...filterDeps],
        queryFn: async () => {
            const params = buildQueryParams();
            return MarketplaceAPI.getAllProjects(params);
        },
        staleTime: 5 * 60 * 1000,
        enabled: needsBulkFetch && !!pageData, // Only fetch all when search is active
    });

    // --- DATA ADAPTATION ---
    const adaptedPageItems = useMemo(() => {
        return (pageData?.items || []).map(adaptCanonicalToProperty);
    }, [pageData?.items]);

    const adaptedAllItems = useMemo(() => 
        (allData?.items || []).map(adaptCanonicalToProperty), 
    [allData?.items]);

    // --- FILTERING & SORTING (Global) ---
    // When search is active, use bulk data. Otherwise, rely on server-side sorting.
    const filteredAndSorted = useMemo(() => {
        const hasFullDataset = needsBulkFetch && !!allData && allData.items.length > 0;
        const baseProperties = hasFullDataset ? adaptedAllItems : adaptedPageItems;

        return baseProperties.filter(property => {
             // 1. Search Query - Search across ALL fields
            if (normalizedFilters.searchQuery) {
                const q = normalizedFilters.searchQuery.toLowerCase();
                const matchesTitle = property.title.toLowerCase().includes(q);
                const matchesLoc = property.location.toLowerCase().includes(q);
                const matchesDev = property.developer.toLowerCase().includes(q);
                const matchesDesc = (property.description || '').toLowerCase().includes(q);
                // Also check if search matches any feature tags
                const matchesFeatures = (property.features || []).some(f => f.toLowerCase().includes(q));
                if (!matchesTitle && !matchesLoc && !matchesDev && !matchesDesc && !matchesFeatures) return false;
            }

            // 2. Numeric Filtering using RANGES
            const propPrice = property.priceNumeric;
            const [minPrice, maxPrice] = normalizedFilters.priceRange;

            // Only filter if price range is not at defaults (0 to 50M)
            if (minPrice > 0 || maxPrice < 50000000) {
                if (propPrice === 0) {
                    // If property has no price and minPrice > 0, exclude it
                    if (minPrice > 0) return false;
                } else {
                    // If property has price, check if it's within range
                    if (propPrice < minPrice || propPrice > maxPrice) return false;
                }
            }

            // 3. Location Filter
            if (normalizedFilters.locations && normalizedFilters.locations.length > 0) {
                if (!property.location) return false;
                const propLocation = property.location.toLowerCase();
                const matchesLocation = normalizedFilters.locations.some(loc => 
                    propLocation.includes(loc.toLowerCase())
                );
                if (!matchesLocation) return false;
            }

            // 4. Property Type (Multi-Select)
            if (normalizedFilters.type.length > 0) {
                if (!property.type) return false; 
                const matchesType = normalizedFilters.type.some(t => property.type?.includes(t));
                if (!matchesType) return false;
            }

            // 5. Beds - use bedroomsAvailable array with fallback
            if (normalizedFilters.beds !== null) {
                // Try to get bedrooms from bedroomsAvailable array
                let availableBeds = property.bedroomsAvailable || [];
                
                // Fallback: derive from unitTypes if bedroomsAvailable is empty
                if (availableBeds.length === 0 && property.unitTypes && property.unitTypes.length > 0) {
                    availableBeds = [...new Set(property.unitTypes.map(u => u.beds || 0))].sort((a, b) => a - b);
                }
                
                // If we still have no bedroom data, include the property (don't exclude)
                // This prevents false negatives when data is incomplete
                if (availableBeds.length === 0) {
                    // Only exclude if we're filtering for a specific bedroom count and have no data
                    // For now, include properties with missing bedroom data to avoid over-filtering
                    // return false; // Commented out to be more lenient
                } else {
                    // We have bedroom data, apply the filter
                    if (normalizedFilters.beds >= 4) {
                        // 4+ means any bedroom >= 4
                        if (!availableBeds.some(b => b >= 4)) return false;
                    } else {
                        // Exact match for 0, 1, 2, 3
                        if (!availableBeds.includes(normalizedFilters.beds)) return false;
                    }
                }
            }

            // 6. Size (Ranges)
            const propSqft = property.sqftNumeric;
            const [minSqft, maxSqft] = normalizedFilters.sqftRange;
            // Only filter if sqft range is not at defaults (0 to 10000)
            if (minSqft > 0 || maxSqft < 10000) { 
                if (propSqft === 0) {
                    // If property has no sqft and minSqft > 0, exclude it
                    if (minSqft > 0) return false;
                } else {
                    // If property has sqft, check if it's within range
                    // For maxSqft = 10000, treat it as "10000 or more" (no upper limit)
                    if (propSqft < minSqft) return false;
                    if (maxSqft < 10000 && propSqft > maxSqft) return false;
                }
            }

            // 6b. Price Per Sqft (PPSF) Filtering
            const propPpsf = property.pricePerSqft;
            const [minPpsf, maxPpsf] = normalizedFilters.ppsfRange;
            // Only filter if ppsf range is not at defaults (0 to 20000)
            if (minPpsf > 0 || maxPpsf < 20000) {
                if (propPpsf === 0 || !propPpsf) {
                    // If property has no ppsf and minPpsf > 0, exclude it
                    if (minPpsf > 0) return false;
                } else {
                    // If property has ppsf, check if it's within range
                    // For maxPpsf = 20000, treat it as "20000 or more" (no upper limit)
                    if (propPpsf < minPpsf) return false;
                    if (maxPpsf < 20000 && propPpsf > maxPpsf) return false;
                }
            }
            
            // 7. Completion (Multi-Select)
            if (normalizedFilters.completion && normalizedFilters.completion.length > 0 && !normalizedFilters.completion.includes('Any')) {
                const matchesAny = normalizedFilters.completion.some(selectedOption => {
                    if (selectedOption === 'Ready') {
                       // Ready = completion is in the past or construction_status is 'ready'
                       // 60 days buffer for 'just handed over'
                       return property.completionTimestamp && property.completionTimestamp <= Date.now() + (60 * 24 * 60 * 60 * 1000);
                    } else {
                        // Filter by year
                        const filterYear = parseInt(selectedOption);
                        if (!isNaN(filterYear)) {
                            if (!property.completionTimestamp) return false;
                            const completionYear = new Date(property.completionTimestamp).getFullYear();
                            return completionYear === filterYear;
                        }
                    }
                    return false;
                });
                
                if (!matchesAny) return false;
            }
            
            // 8. Developer
            if (normalizedFilters.developer && normalizedFilters.developer !== 'All Developers' && property.developer !== normalizedFilters.developer) {
                return false;
            }

            return true;
        }).sort((a, b) => {
            const isASoldOut = a.badge.text === 'SOLD OUT';
            const isBSoldOut = b.badge.text === 'SOLD OUT';

            // Debug Log
            if (sortOption.startsWith('price')) {
                 // console.log(`Sorting ${sortOption}: ${a.priceNumeric} vs ${b.priceNumeric}`);
            }

            if (isASoldOut && !isBSoldOut) return 1;
            if (!isASoldOut && isBSoldOut) return -1;

            if (sortOption === 'price_asc') {
                 const pA = (a.priceNumeric || 0) === 0 ? Infinity : (a.priceNumeric || 0);
                 const pB = (b.priceNumeric || 0) === 0 ? Infinity : (b.priceNumeric || 0);
                 return pA - pB;
            } else if (sortOption === 'price_desc') {
                 const pA = (a.priceNumeric || 0) === 0 ? -Infinity : (a.priceNumeric || 0);
                 const pB = (b.priceNumeric || 0) === 0 ? -Infinity : (b.priceNumeric || 0);
                 return pB - pA;
            } else if (sortOption === 'date') {
                 const dateA = new Date(processDate(a.createdAt)).getTime();
                 const dateB = new Date(processDate(b.createdAt)).getTime();
                 if (dateA !== dateB) return dateB - dateA;
                 return Number(b.id) - Number(a.id);
            }
            return 0;
        });
    }, [adaptedAllItems, adaptedPageItems, allData, normalizedFilters, sortOption, needsBulkFetch]);

    // --- PAGINATION ---
    const hasFullDataset = !!allData && allData.items.length > 0;
    const totalPages = hasFullDataset
        ? Math.ceil(filteredAndSorted.length / PAGE_SIZE) || 1
        : (pageData?.totalPages || 1);
    
    useEffect(() => {
        setCurrentPage(1);
    }, [filters, sortOption]);

    // Scroll to top when page changes
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [currentPage]);

    const paginatedProperties = useMemo(() => {
        if (!hasFullDataset) return filteredAndSorted;
        const start = (currentPage - 1) * PAGE_SIZE;
        return filteredAndSorted.slice(start, start + PAGE_SIZE);
    }, [filteredAndSorted, currentPage, hasFullDataset]);

    return {
        properties: paginatedProperties,
        allPropertiesRaw: hasFullDataset ? adaptedAllItems : adaptedPageItems,
        isLoading: isPagePending, // Essential: Only shows skeletons on the first real load
        isFetching: isPageFetching || isAllFetching,
        hasLoadedOnce: !!pageData,
        isError: isPageError,
        filters,
        setFilters,
        resetFilters: () => setFilters(initialFilters),
        sortOption,
        setSortOption,
        currentPage,
        setCurrentPage,
        totalPages,
        setAllProperties: (val: any) => {} // Shim to avoid breaking App.tsx for now
    };
};
